import { useState, useRef, useEffect, useCallback } from 'react'
import { Message } from '../types'

/**
 * Hook for managing auto-scroll behavior in the chat.
 * Tracks whether user has manually scrolled up and handles auto-scrolling to bottom.
 */
export function useScrollManagement(messages: Message[], isLoading: boolean) {
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Check if user is at the bottom of the messages container
  const isAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
    return scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold
  }, [])

  // Handle scroll events to detect if user has manually scrolled up
  const handleScroll = useCallback(() => {
    if (isAtBottom()) {
      setUserHasScrolledUp(false)
    } else {
      setUserHasScrolledUp(true)
    }
  }, [isAtBottom])

  // Auto-scroll to bottom when new messages arrive, unless user has scrolled up
  useEffect(() => {
    if (!userHasScrolledUp) {
      scrollToBottom()
    }
  }, [messages, userHasScrolledUp, scrollToBottom])

  // Reset scroll state when loading starts (new message being generated)
  useEffect(() => {
    if (isLoading) {
      setUserHasScrolledUp(false)
    }
  }, [isLoading])

  return {
    messagesEndRef,
    messagesContainerRef,
    handleScroll,
    scrollToBottom
  }
}